import React, { useEffect, useState, useMemo, useRef } from "react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import Mandala from "../components/decor/Mandala";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, IndianRupee, Printer, Copy, ShieldCheck, Trash2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { createSevaPayment, fetchPaymentStatus, fetchSevaBookingOptions } from "../api";
import { API_CONFIG } from "../api/config";

const genBookingId = () => "SEVA-" + Math.random().toString(36).slice(2, 8).toUpperCase();
const todayISO = () => new Date().toISOString().slice(0, 10);
const formatLongDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const dateToEpoch = (iso) => new Date(`${iso}T00:00:00`).getTime();
const numbersOnly = (value) => value.replace(/\D/g, "");
const ALLOWED_PAYMENT_MODES = new Set(["CREDIT_CARD", "DEBIT_CARD", "UPI", "NET_BANKING"]);

const StepPill = ({ n, label, active, done }) => (
  <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
    <div className={`w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold border ${done ? "bg-[#B65A14] text-[#FBF7F0] border-[#B65A14]" : active ? "bg-[#D4AF37] text-[#3B2A1A] border-[#D4AF37]" : "bg-[#FBF7F0] text-[#3B2A1A]/60 border-[#D4AF37]/40"}`}>{done ? <CheckCircle2 className="w-4 h-4" /> : n}</div>
    <span className={`min-w-0 text-[11px] sm:text-sm leading-tight ${active || done ? "text-[#3B2A1A] font-semibold" : "text-[#3B2A1A]/60"}`}>{label}</span>
  </div>
);

const RequiredMark = () => <span className="text-[#A81F1F]" aria-hidden="true"> *</span>;
const fieldControlClass = "mt-1 block w-full min-w-0 max-w-full box-border rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]";
const dateFieldControlClass = `${fieldControlClass} appearance-none`;

const COPY = {
  step: { en: "Step", kn: "ಹಂತ" },
  chooseSevas: { en: "Choose Sevas", kn: "ಸೇವೆಗಳನ್ನು ಆಯ್ಕೆಮಾಡಿ" },
  devoteeDetails: { en: "Devotee Details", kn: "ಭಕ್ತರ ವಿವರಗಳು" },
  payment: { en: "Payment", kn: "ಪಾವತಿ" },
  confirmation: { en: "Confirmation", kn: "ದೃಢೀಕರಣ" },
  sevaSingular: { en: "seva", kn: "ಸೇವೆ" },
  sevaPlural: { en: "sevas", kn: "ಸೇವೆಗಳು" },
  selected: { en: "Selected", kn: "ಆಯ್ಕೆಯಾಗಿದೆ" },
  add: { en: "Add", kn: "ಸೇರಿಸಿ" },
  remove: { en: "Remove", kn: "ತೆಗೆದುಹಾಕಿ" },
  fullName: { en: "Full name", kn: "ಪೂರ್ಣ ಹೆಸರು" },
  email: { en: "Email address", kn: "ಇಮೇಲ್ ವಿಳಾಸ" },
  phone: { en: "Phone number", kn: "ದೂರವಾಣಿ ಸಂಖ್ಯೆ" },
  sevaDate: { en: "Seva date", kn: "ಸೇವಾ ದಿನಾಂಕ" },
  gothra: { en: "Gothra", kn: "ಗೋತ್ರ" },
  nakshatra: { en: "Nakshatra", kn: "ನಕ್ಷತ್ರ" },
  rashi: { en: "Rashi", kn: "ರಾಶಿ" },
  chooseGothra: { en: "Choose Gothra", kn: "ಗೋತ್ರ ಆಯ್ಕೆಮಾಡಿ" },
  chooseNakshatra: { en: "Choose Nakshatra", kn: "ನಕ್ಷತ್ರ ಆಯ್ಕೆಮಾಡಿ" },
  chooseRashi: { en: "Choose Rashi", kn: "ರಾಶಿ ಆಯ್ಕೆಮಾಡಿ" },
  collectPrasadam: { en: "Collect Prasadam", kn: "ಪ್ರಸಾದ ಸ್ವೀಕರಿಸುವ ವಿಧಾನ" },
  personalVisit: { en: "By Personal Visit", kn: "ಸ್ವತಃ ಭೇಟಿ ನೀಡಿ" },
  byPost: { en: "By Post", kn: "ಅಂಚೆ ಮೂಲಕ" },
  visitDate: { en: "Date of Visit", kn: "ಭೇಟಿ ದಿನಾಂಕ" },
  address: { en: "Address", kn: "ವಿಳಾಸ" },
  postalAddress: { en: "Postal address", kn: "ಅಂಚೆ ವಿಳಾಸ" },
  city: { en: "City", kn: "ನಗರ" },
  state: { en: "State", kn: "ರಾಜ್ಯ" },
  pincode: { en: "Pincode", kn: "ಪಿನ್ ಕೋಡ್" },
  paymentMode: { en: "Payment mode", kn: "ಪಾವತಿ ವಿಧಾನ" },
  choosePaymentMode: { en: "Select payment mode", kn: "ಪಾವತಿ ವಿಧಾನ ಆಯ್ಕೆಮಾಡಿ" },
  proceed: { en: "Proceed", kn: "ಮುಂದೆ ಸಾಗಿ" },
  proceedToPay: { en: "Proceed to pay", kn: "ಪಾವತಿಗೆ ಮುಂದುವರಿಯಿರಿ" },
  back: { en: "Back", kn: "ಹಿಂದೆ" },
  confirmPay: { en: "Confirm & Pay", kn: "ದೃಢೀಕರಿಸಿ ಮತ್ತು ಪಾವತಿಸಿ" },
  secureCheckout: { en: "A secure Cashfree checkout will open after creating your payment session.", kn: "ಪಾವತಿ ಸೆಷನ್ ಸೃಷ್ಟಿಯಾದ ನಂತರ ಸುರಕ್ಷಿತ Cashfree checkout ತೆರೆಯುತ್ತದೆ." },
  startingPayment: { en: "Starting payment...", kn: "ಪಾವತಿ ಆರಂಭವಾಗುತ್ತಿದೆ..." },
  pay: { en: "Pay", kn: "ಪಾವತಿಸಿ" },
  bookedSuccess: { en: "Seva booked & payment successful", kn: "ಸೇವೆ ಬುಕಿಂಗ್ ಮತ್ತು ಪಾವತಿ ಯಶಸ್ವಿಯಾಗಿದೆ" },
  bookingId: { en: "Booking ID", kn: "ಬುಕಿಂಗ್ ಸಂಖ್ಯೆ" },
  copy: { en: "Copy", kn: "ನಕಲಿಸಿ" },
  totalPaid: { en: "Total paid", kn: "ಪಾವತಿಸಿದ ಒಟ್ಟು" },
  devotee: { en: "Devotee", kn: "ಭಕ್ತ" },
  prasadam: { en: "Prasadam", kn: "ಪ್ರಸಾದ" },
  cityState: { en: "City / State", kn: "ನಗರ / ರಾಜ್ಯ" },
  bookMore: { en: "Book more sevas", kn: "ಇನ್ನಷ್ಟು ಸೇವೆಗಳನ್ನು ಬುಕ್ ಮಾಡಿ" },
};

const Sevas = () => {
  const { t, lang } = useLang();
  const L = (copy) => (copy && copy[lang]) || copy.en;
  const [step, setStep] = useState("choose");
  const [sevas, setSevas] = useState([]);
  const [zodiacSigns, setZodiacSigns] = useState([]);
  const [nakshatras, setNakshatras] = useState([]);
  const [gothras, setGothras] = useState([]);
  const [paymentChannels, setPaymentChannels] = useState([]);
  const [sevasLoading, setSevasLoading] = useState(true);
  const [sevasError, setSevasError] = useState("");
  const [selectedSevas, setSelectedSevas] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", sevaDate: todayISO(), gothra: "", nakshatra: "", zodiacSign: "", deliveryOption: "2", visitDate: "", address: "", city: "", state: "", pincode: "", paymentMode: "" });
  const [errors, setErrors] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const formSectionRef = useRef(null);
  const paySectionRef = useRef(null);

  useEffect(() => {
    let alive = true;

    const loadSevaOptions = async () => {
      try {
        const options = await fetchSevaBookingOptions();
        if (!alive) return;
        setSevas(options.sevas);
        setZodiacSigns(options.zodiacSigns);
        setNakshatras(options.nakshatras);
        setGothras(options.gothras);
        setPaymentChannels(
          options.paymentChannels.filter((channel) =>
            ALLOWED_PAYMENT_MODES.has(channel.name) ||
            ALLOWED_PAYMENT_MODES.has(String(channel.label).replace(/\s+/g, "_").toUpperCase())
          )
        );
        setSevasError("");
      } catch (error) {
        if (!alive) return;
        setSevasError("Unable to load sevas right now.");
      } finally {
        if (alive) {
          setSevasLoading(false);
        }
      }
    };

    loadSevaOptions();

    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!["form", "pay"].includes(step)) return;

    const frame = requestAnimationFrame(() => {
      const target = step === "pay" ? paySectionRef.current : formSectionRef.current;
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    return () => cancelAnimationFrame(frame);
  }, [step]);

  const toggleSeva = (s) => {
    setSelectedSevas((prev) => prev.find((x) => x.id === s.id) ? prev.filter((x) => x.id !== s.id) : [...prev, s]);
  };
  const removeSeva = (id) => setSelectedSevas((prev) => prev.filter((x) => x.id !== id));

  const totalAmount = useMemo(() => selectedSevas.reduce((sum, s) => sum + s.amount, 0), [selectedSevas]);
  const isFormReady = useMemo(() => {
    if (!form.name || form.name.trim().length < 3) return false;
    if (!/^\d{10}$/.test(form.phone)) return false;
    if (!form.sevaDate) return false;
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return false;
    if (!form.paymentMode) return false;

    if (form.deliveryOption === "1") {
      return !!form.visitDate;
    }

    return (
      !!form.address &&
      form.address.trim().length >= 5 &&
      !!form.city &&
      form.city.trim().length >= 2 &&
      !!form.state &&
      form.state.trim().length >= 2 &&
      /^\d{6}$/.test(form.pincode)
    );
  }, [form]);

  const proceedToForm = () => {
    if (selectedSevas.length === 0) { toast.error("Please select at least one seva."); return; }
    setErrors({});
    setStep("form");
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 3) e.name = "Please enter your full name.";
    if (!/^\d{10}$/.test(form.phone)) e.phone = "Enter a valid 10 digit mobile number.";
    if (!form.sevaDate) e.sevaDate = "Please choose a seva date.";
    if (form.deliveryOption === "1" && !form.visitDate) e.visitDate = "Please choose your date of visit.";
    if (form.deliveryOption === "2") {
      if (!form.address || form.address.trim().length < 5) e.address = "Please enter your address.";
      if (!form.city || form.city.trim().length < 2) e.city = "Please enter your city.";
      if (!form.state || form.state.trim().length < 2) e.state = "Please enter your state.";
      if (!/^\d{6}$/.test(form.pincode)) e.pincode = "Please enter a valid 6 digit pincode.";
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.paymentMode) e.paymentMode = "Please choose a payment mode.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const proceedToPay = () => {
    if (!validate()) return;
    setStep("pay");
  };

  const optionName = (options, id) => options.find((option) => option.id === id)?.name || "";
  const selectedPayment = paymentChannels.find((channel) => String(channel.id) === String(form.paymentMode));

  const buildPaymentPayload = () => {
    const sevaTypes = selectedSevas.reduce((acc, seva) => {
      acc[seva.id] = (seva.fundGroups?.length ? seva.fundGroups : [{ fundGroupId: 1, amount: seva.amount }]).reduce((funds, fundGroup) => {
        funds[fundGroup.fundGroupId] = fundGroup.amount;
        return funds;
      }, {});
      return acc;
    }, {});

    return {
      sevaTypes,
      amount: totalAmount,
      devoteeName: form.name,
      email: form.email,
      phone: numbersOnly(form.phone),
      remarks: "",
      paymentChannelId: Number(form.paymentMode),
      executionDate: dateToEpoch(form.sevaDate),
      deliveryOption: Number(form.deliveryOption),
      prasadaDeliveryDate: form.deliveryOption === "1" && form.visitDate ? dateToEpoch(form.visitDate) : null,
      address: form.deliveryOption === "2" ? form.address : "",
      transactionReference: null,
      institutionReference: API_CONFIG.sevasInstitutionReference,
      sevaCategoryId: 1,
      zodiacSignId: form.zodiacSign ? Number(form.zodiacSign) : null,
      nakshatraId: form.nakshatra ? Number(form.nakshatra) : null,
      gothraId: form.gothra ? Number(form.gothra) : null,
      gothra: null,
      city: form.deliveryOption === "2" ? form.city : "",
      state: form.deliveryOption === "2" ? form.state : "",
      pincode: form.deliveryOption === "2" ? form.pincode : "",
      materialDonationId: null,
      toPaymentInstrumentId: null,
      fromBankMasterId: null,
      panNumber: "",
      dob: "",
      sevaDescription: null,
      originalSevaAmount: "",
      devoteeNameDup: "",
      isDesktop: true,
    };
  };

  const completePayment = async () => {
    if (paymentLoading) return;

    if (!window.Cashfree) {
      toast.error("Cashfree payment gateway is not available. Please refresh and try again.");
      return;
    }

    setPaymentLoading(true);
    try {
      const paymentData = await createSevaPayment(buildPaymentPayload());
      if (!paymentData?.paymentSessionId) {
        throw new Error("Payment Session ID is missing.");
      }
      console.log('cashfreeMode: ', API_CONFIG.cashfreeMode)

      const cashfree = window.Cashfree({ mode: API_CONFIG.cashfreeMode });
      const result = await cashfree.checkout({
        paymentSessionId: paymentData.paymentSessionId,
        redirectTarget: "_modal",
      });

      if (result?.error) {
        toast.error("Payment was cancelled or failed.");
        return;
      }

      if (result?.redirect) {
        toast.info("Payment redirected. Please complete the payment in the opened window.");
        return;
      }

      if (paymentData.reference && result?.paymentDetails) {
        const status = await fetchPaymentStatus(paymentData.reference);
        if (status.toLowerCase() !== "success") {
          toast.error(`Payment status: ${status}`);
          return;
        }
      }

      const record = {
        bookingId: paymentData.reference || genBookingId(),
        sevas: selectedSevas,
        totalAmount,
        ...form,
        gothraName: optionName(gothras, form.gothra),
        nakshatraName: optionName(nakshatras, form.nakshatra),
        zodiacSignName: optionName(zodiacSigns, form.zodiacSign),
        paymentLabel: selectedPayment?.label || form.paymentMode,
        paidAt: new Date().toISOString(),
      };
      setConfirmation(record);
      setStep("done");
      toast.success(`Payment successful. Booking ID: ${record.bookingId}`);
    } catch (error) {
      toast.error(error.message || "Unable to start payment.");
    } finally {
      setPaymentLoading(false);
    }
  };

  const restart = () => {
    setStep("choose");
    setSelectedSevas([]);
    setForm({ name: "", email: "", phone: "", sevaDate: todayISO(), gothra: "", nakshatra: "", zodiacSign: "", deliveryOption: "2", visitDate: "", address: "", city: "", state: "", pincode: "", paymentMode: "" });
    setErrors({});
    setConfirmation(null);
  };

  return (
    <PageLayout>
      <PageHero eyebrow={t("sevas.hero.eyebrow")} title={t("sevas.hero.title")} />

      {/* Stepper */}
      <section className="py-3 sm:py-6 bg-[#FBF7F0]/80 border-b border-[#D4AF37]/20 sticky top-0 z-20 backdrop-blur-sm" data-testid="seva-stepper">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 lg:px-8 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-3 sm:gap-6">
          <StepPill n="1" label={L(COPY.chooseSevas)} active={step === "choose"} done={step !== "choose"} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="2" label={L(COPY.devoteeDetails)} active={step === "form"} done={["pay", "done"].includes(step)} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="3" label={L(COPY.payment)} active={step === "pay"} done={step === "done"} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="4" label={L(COPY.confirmation)} active={step === "done"} done={step === "done"} />
        </div>
      </section>

      {/* STEP 1 - multi-select */}
      {step === "choose" && (
        <section className="py-10 sm:py-16" data-testid="seva-choose">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-3 gap-8 min-w-0">
            <div className="lg:col-span-2 min-w-0">
              <SectionHeading eyebrow={`${L(COPY.step)} 1`} title={t("sevas.step1.title")} />
              <p className="mt-2 text-sm text-[#3B2A1A]/70">{t("sevas.step1.desc")}</p>
              {sevasLoading && (
                <div className="mt-8 grid sm:grid-cols-2 gap-4" aria-live="polite">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div key={index} className="rounded-2xl p-5 border-2 border-[#D4AF37]/30 bg-[#FBF7F0] animate-pulse">
                      <div className="h-5 w-5/6 rounded bg-[#3B2A1A]/10" />
                      <div className="mt-3 h-4 w-2/3 rounded bg-[#3B2A1A]/10" />
                      <div className="mt-5 h-6 w-24 rounded bg-[#D4AF37]/30" />
                    </div>
                  ))}
                </div>
              )}
              {!sevasLoading && sevasError && (
                <div className="mt-8 rounded-2xl border border-[#D4AF37]/30 bg-[#FBF7F0] px-5 py-4 text-sm text-[#3B2A1A]/80">
                  {sevasError}
                </div>
              )}
              {!sevasLoading && !sevasError && (
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  {sevas.map((s) => {
                  const selected = selectedSevas.some((x) => x.id === s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleSeva(s)}
                      aria-pressed={selected}
                      className={`w-full min-w-0 text-left rounded-2xl p-4 sm:p-5 card-lift transition-all relative border-2 overflow-hidden ${selected ? "border-[#B65A14] bg-[#F2E7D5]" : "border-[#D4AF37]/30 bg-[#FBF7F0] hover:border-[#B65A14]/50"}`}
                      data-testid={`seva-${s.id}`}
                    >
                      {selected && (
                        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#B65A14] text-[#FBF7F0] flex items-center justify-center" data-testid={`seva-check-${s.id}`}>
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                      )}
                      <div className="font-kannada text-[#3B2A1A] text-base leading-snug pr-8 break-words" style={{ fontFamily: "'Noto Sans Kannada', serif", overflowWrap: "anywhere" }}>{s.name}</div>
                      {s.description && <div className="text-sm text-[#3B2A1A]/70 mt-1 pr-8 break-words" style={{ overflowWrap: "anywhere" }}>{s.description}</div>}
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="font-serif-display text-xl text-[#B65A14] flex items-center"><IndianRupee className="w-4 h-4" />{s.amount.toLocaleString("en-IN")}</div>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold text-right ${selected ? "text-[#8F4610]" : "text-[#B65A14]"}`}>{selected ? L(COPY.selected) : L(COPY.add)}</span>
                      </div>
                    </button>
                  );
                  })}
                </div>
              )}
            </div>

            {/* Basket */}
            <aside className="bg-gradient-to-b from-[#FBF7F0] to-[#F2E7D5] border border-[#D4AF37]/30 rounded-2xl p-5 sm:p-6 h-fit lg:sticky lg:top-24" data-testid="seva-basket">
              <div className="font-cinzel text-xs tracking-widest text-[#B65A14]">{t("sevas.basket.title")}</div>
              <div className="mt-3 text-xl sm:text-2xl font-serif-display text-[#3B2A1A] leading-tight">{selectedSevas.length} {selectedSevas.length === 1 ? L(COPY.sevaSingular) : L(COPY.sevaPlural)} {t("sevas.basket.selected")}</div>
              <div className="mt-5 space-y-2 max-h-72 overflow-y-auto pr-1">
                {selectedSevas.length === 0 && (
                  <div className="text-sm text-[#3B2A1A]/60 italic">{t("sevas.basket.empty")}</div>
                )}
                {selectedSevas.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-xl px-3 py-2 min-w-0" data-testid={`basket-${s.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-[#3B2A1A] break-words" style={{ overflowWrap: "anywhere" }}>{s.name}</div>
                      <div className="text-xs text-[#B65A14] flex items-center"><IndianRupee className="w-3 h-3" />{s.amount.toLocaleString("en-IN")}</div>
                    </div>
                    <button onClick={() => removeSeva(s.id)} className="ml-3 w-8 h-8 rounded-full text-[#8F4610] hover:bg-[#F2E7D5] flex items-center justify-center" aria-label={`${L(COPY.remove)} ${s.name}`} data-testid={`basket-remove-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-[#D4AF37]/40 flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-widest text-[#3B2A1A]/70">{t("sevas.basket.total")}</span>
                <span className="font-serif-display text-2xl text-[#3B2A1A] flex items-center" data-testid="seva-total"><IndianRupee className="w-5 h-5" />{totalAmount.toLocaleString("en-IN")}</span>
              </div>
              <button
                onClick={proceedToForm}
                disabled={selectedSevas.length === 0}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3 text-sm font-semibold hover:bg-[#8F4610] disabled:bg-[#B65A14]/40 disabled:cursor-not-allowed shadow"
                data-testid="seva-proceed-basket"
              >
                {L(COPY.proceed)} <ArrowRight className="w-4 h-4" />
              </button>
            </aside>
          </div>
        </section>
      )}

      {/* STEP 2 form */}
      {step === "form" && selectedSevas.length > 0 && (
        <section ref={formSectionRef} className="py-10 sm:py-16 scroll-mt-24 sm:scroll-mt-28" data-testid="seva-form">
          <div className="max-w-4xl mx-auto px-4 lg:px-8 grid md:grid-cols-5 gap-6">
            <div className="md:col-span-3 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-5 sm:p-8 min-w-0">
              <SectionHeading eyebrow={`${L(COPY.step)} 2`} title={t("sevas.step2.title")} />
              <div className="mt-6 space-y-4">
                {[
                  { key: "name", label: L(COPY.fullName), placeholder: L(COPY.fullName) },
                  { key: "email", label: L(COPY.email), placeholder: "name@example.com", type: "email" },
                  { key: "phone", label: L(COPY.phone), placeholder: "98xxxxxxxx" },
                  { key: "sevaDate", label: L(COPY.sevaDate), type: "date" },
                  { key: "gothra", label: L(COPY.gothra), placeholder: L(COPY.chooseGothra), options: gothras },
                  { key: "nakshatra", label: L(COPY.nakshatra), placeholder: L(COPY.chooseNakshatra), options: nakshatras },
                  { key: "zodiacSign", label: L(COPY.rashi), placeholder: L(COPY.chooseRashi), options: zodiacSigns },
                ].map((f) => (
                  <div key={f.key} className="min-w-0">
                    <label className="text-sm font-semibold text-[#3B2A1A]">
                      {f.label}{["name", "phone", "sevaDate"].includes(f.key) && <RequiredMark />}
                    </label>
                    {f.multiline ? (
                      <textarea value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} rows={3} className={fieldControlClass} data-testid={`seva-${f.key}-input`} />
                    ) : f.options ? (
                      <select value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={fieldControlClass} data-testid={`seva-${f.key}-input`}>
                        <option value="">{f.placeholder}</option>
                        {f.options.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.nativeName ? `${option.name} (${option.nativeName})` : option.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={f.type || "text"}
                        value={form[f.key]}
                        onChange={(e) => setForm({ ...form, [f.key]: f.key === "phone" ? numbersOnly(e.target.value).slice(0, 10) : e.target.value })}
                        onKeyDown={(e) => {
                          if (f.key === "phone" && e.key.length === 1 && !/\d/.test(e.key)) e.preventDefault();
                        }}
                        inputMode={f.key === "phone" ? "numeric" : undefined}
                        maxLength={f.key === "phone" ? 10 : undefined}
                        placeholder={f.placeholder}
                        min={f.type === "date" ? todayISO() : undefined}
                        className={f.type === "date" ? dateFieldControlClass : fieldControlClass}
                        data-testid={`seva-${f.key}-input`}
                      />
                    )}
                    {errors[f.key] && <p className="text-xs text-[#A81F1F] mt-1" data-testid={`seva-err-${f.key}`}>{errors[f.key]}</p>}
                  </div>
                ))}
                <div>
                  <label className="text-sm font-semibold text-[#3B2A1A]">{L(COPY.collectPrasadam)}<RequiredMark /></label>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2">
                    {[
                      { id: "1", label: L(COPY.personalVisit) },
                      { id: "2", label: L(COPY.byPost) },
                    ].map((option) => (
                      <label key={option.id} className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer ${form.deliveryOption === option.id ? "border-[#B65A14] bg-[#F2E7D5]/60" : "border-[#D4AF37]/40 bg-[#FBF7F0]"}`}>
                        <input type="radio" name="deliveryOption" value={option.id} checked={form.deliveryOption === option.id} onChange={() => setForm({ ...form, deliveryOption: option.id })} data-testid={`seva-delivery-${option.id}`} />
                        <span className="text-sm text-[#3B2A1A]">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {form.deliveryOption === "1" && (
                  <div className="min-w-0">
                    <label className="text-sm font-semibold text-[#3B2A1A]">{L(COPY.visitDate)}<RequiredMark /></label>
                    <input type="date" value={form.visitDate} onChange={(e) => setForm({ ...form, visitDate: e.target.value })} min={todayISO()} className={dateFieldControlClass} data-testid="seva-visitDate-input" />
                    {errors.visitDate && <p className="text-xs text-[#A81F1F] mt-1" data-testid="seva-err-visitDate">{errors.visitDate}</p>}
                  </div>
                )}
                {form.deliveryOption === "2" && (
                  <>
                    {[
                      { key: "address", label: L(COPY.address), placeholder: L(COPY.postalAddress), multiline: true },
                      { key: "city", label: L(COPY.city), placeholder: "Bangalore" },
                      { key: "state", label: L(COPY.state), placeholder: "Karnataka" },
                      { key: "pincode", label: L(COPY.pincode), placeholder: "560073" },
                    ].map((f) => (
                      <div key={f.key} className="min-w-0">
                        <label className="text-sm font-semibold text-[#3B2A1A]">{f.label}<RequiredMark /></label>
                        {f.multiline ? (
                          <textarea value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} rows={3} className={fieldControlClass} data-testid={`seva-${f.key}-input`} />
                        ) : (
                          <input type="text" value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} className={fieldControlClass} data-testid={`seva-${f.key}-input`} />
                        )}
                        {errors[f.key] && <p className="text-xs text-[#A81F1F] mt-1" data-testid={`seva-err-${f.key}`}>{errors[f.key]}</p>}
                      </div>
                    ))}
                  </>
                )}
                <div className="min-w-0">
                  <label className="text-sm font-semibold text-[#3B2A1A]">{L(COPY.paymentMode)}<RequiredMark /></label>
                  <select
                    value={form.paymentMode}
                    onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}
                    className={fieldControlClass}
                    data-testid="seva-paymentMode-select"
                  >
                    <option value="">{L(COPY.choosePaymentMode)}</option>
                    {paymentChannels.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                  {errors.paymentMode && <p className="text-xs text-[#A81F1F] mt-1" data-testid="seva-err-paymentMode">{errors.paymentMode}</p>}
                </div>
              </div>
              <div className="mt-6 grid sm:flex sm:flex-wrap gap-3">
                <button onClick={() => setStep("choose")} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-6 py-3 text-sm font-semibold hover:bg-[#F2E7D5]" data-testid="seva-back">← {L(COPY.back)}</button>
                <button
                  onClick={proceedToPay}
                  disabled={!isFormReady}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-7 py-3 text-sm font-semibold hover:bg-[#8F4610] disabled:bg-[#B65A14]/40 disabled:cursor-not-allowed shadow"
                  data-testid="seva-proceed"
                >
                  {t("sevas.proceedToPay")} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <aside className="md:col-span-2 bg-gradient-to-b from-[#FBF7F0] to-[#F2E7D5] border border-[#D4AF37]/30 rounded-2xl p-5 sm:p-6 h-fit md:sticky md:top-24 min-w-0" data-testid="seva-summary">
              <div className="font-cinzel text-xs tracking-widest text-[#B65A14]">{L(COPY.selected)} {L(COPY.sevaPlural)} ({selectedSevas.length})</div>
              <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
                {selectedSevas.map((s) => (
                  <div key={s.id} className="flex items-start justify-between gap-2 text-sm min-w-0">
                    <span className="text-[#3B2A1A] min-w-0 break-words" style={{ overflowWrap: "anywhere" }}>{s.name}</span>
                    <span className="text-[#B65A14] whitespace-nowrap flex items-center"><IndianRupee className="w-3 h-3" />{s.amount.toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-[#D4AF37]/40 flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-widest text-[#3B2A1A]/70">{t("sevas.basket.total")}</span>
                <span className="font-serif-display text-2xl text-[#3B2A1A] flex items-center" data-testid="seva-amount"><IndianRupee className="w-5 h-5" />{totalAmount.toLocaleString("en-IN")}</span>
              </div>
            </aside>
          </div>
        </section>
      )}

      {/* STEP 3 payment */}
      {step === "pay" && selectedSevas.length > 0 && (
        <section ref={paySectionRef} className="py-10 sm:py-16 scroll-mt-24 sm:scroll-mt-28" data-testid="seva-pay">
          <div className="max-w-2xl mx-auto px-4 lg:px-8">
            <div className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-5 sm:p-8 shadow-lg text-center">
              <ShieldCheck className="w-12 h-12 text-[#B65A14] mx-auto" />
              <h3 className="mt-4 font-serif-display text-2xl text-[#3B2A1A]">{t("sevas.confirm.title")}</h3>
              <p className="mt-2 text-sm text-[#3B2A1A]/75">{lang === "kn" ? "ನೀವು ಪಾವತಿಸುವ ಮೊತ್ತ" : "You will pay"} <span className="font-bold">₹{totalAmount.toLocaleString("en-IN")}</span> {lang === "kn" ? "ಆಯ್ಕೆ ಮಾಡಿದ ಸೇವೆಗಳಿಗೆ" : "for"} <span className="font-semibold">{selectedSevas.length} {selectedSevas.length === 1 ? L(COPY.sevaSingular) : L(COPY.sevaPlural)}</span> {lang === "kn" ? "ಮೂಲಕ" : "via"} <span className="font-semibold uppercase">{selectedPayment?.label || form.paymentMode}</span>.</p>
              <div className="mt-6 p-4 bg-[#F2E7D5]/70 border border-[#D4AF37]/30 rounded-xl text-xs text-[#3B2A1A]/85">{L(COPY.secureCheckout)}</div>
              <div className="mt-6 grid sm:flex sm:flex-wrap sm:justify-center gap-3">
                <button onClick={() => setStep("form")} disabled={paymentLoading} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-6 py-3 text-sm font-semibold disabled:opacity-50" data-testid="seva-pay-back">← {L(COPY.back)}</button>
                <button onClick={completePayment} disabled={paymentLoading} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-7 py-3 text-sm font-semibold hover:bg-[#8F4610] disabled:bg-[#B65A14]/50 disabled:cursor-not-allowed shadow" data-testid="seva-pay-now">
                  {paymentLoading ? L(COPY.startingPayment) : `${L(COPY.pay)} ₹${totalAmount.toLocaleString("en-IN")}`} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* STEP 4 confirmation */}
      {step === "done" && confirmation && (
        <section className="py-10 sm:py-16" data-testid="seva-done">
          <div className="max-w-3xl mx-auto px-4 lg:px-8">
            <div className="bg-[#FBF7F0] border border-[#D4AF37]/40 rounded-2xl p-5 sm:p-8 relative overflow-hidden shadow-lg">
              <div className="absolute -top-8 -right-8 pointer-events-none"><Mandala size={200} color="#D4AF37" opacity={0.35} /></div>
              <div className="flex items-start sm:items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/30 flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-[#8F4610]" /></div>
                <div className="min-w-0"><div className="overline text-[#B65A14]">Sri Sode Vadiraja Matha</div><h3 className="font-serif-display text-2xl sm:text-3xl text-[#3B2A1A] leading-tight">{t("sevas.done.title")}</h3></div>
              </div>
              <div className="mt-6 grid sm:flex sm:items-center sm:justify-between gap-3 bg-[#F2E7D5]/70 border border-[#D4AF37]/40 rounded-xl px-4 py-3">
                <div className="min-w-0"><div className="text-[10px] uppercase tracking-widest text-[#3B2A1A]/70">{L(COPY.bookingId)}</div><div className="font-cinzel text-base sm:text-lg text-[#3B2A1A] break-all" data-testid="seva-conf-id">{confirmation.bookingId}</div></div>
                <button onClick={() => { navigator.clipboard?.writeText(confirmation.bookingId); toast.success("Copied"); }} className="inline-flex items-center justify-center gap-1 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-3 py-1.5 text-xs font-semibold" data-testid="seva-copy"><Copy className="w-3.5 h-3.5" /> {L(COPY.copy)}</button>
              </div>
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-widest text-[#3B2A1A]/70">{L(COPY.sevaPlural)} ({confirmation.sevas.length})</div>
                <div className="mt-2 divide-y divide-[#D4AF37]/30 border border-[#D4AF37]/30 rounded-xl">
                  {confirmation.sevas.map((s) => (
                    <div key={s.id} className="flex items-start justify-between gap-3 px-4 py-2.5" data-testid={`conf-seva-${s.id}`}>
                      <span className="text-sm text-[#3B2A1A] min-w-0">{s.name}</span>
                      <span className="text-sm font-semibold text-[#3B2A1A] flex items-center"><IndianRupee className="w-3.5 h-3.5" />{s.amount.toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-[#F2E7D5]/60">
                    <span className="text-sm font-bold text-[#3B2A1A]">{L(COPY.totalPaid)}</span>
                    <span className="text-base font-bold text-[#3B2A1A] flex items-center"><IndianRupee className="w-4 h-4" />{confirmation.totalAmount.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>
              <dl className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.devotee)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.name}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.phone)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.phone}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.sevaDate)}</dt><dd className="font-semibold text-[#3B2A1A]">{formatLongDate(confirmation.sevaDate)}</dd></div>
                {(confirmation.gothraName || confirmation.nakshatraName) && <div><dt className="text-[#3B2A1A]/70">{L(COPY.gothra)} / {L(COPY.nakshatra)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.gothraName || "-"} / {confirmation.nakshatraName || "-"}</dd></div>}
                {confirmation.zodiacSignName && <div><dt className="text-[#3B2A1A]/70">{L(COPY.rashi)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.zodiacSignName}</dd></div>}
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.prasadam)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.deliveryOption === "1" ? L(COPY.personalVisit) : L(COPY.byPost)}</dd></div>
                {confirmation.deliveryOption === "1" && <div><dt className="text-[#3B2A1A]/70">{L(COPY.visitDate)}</dt><dd className="font-semibold text-[#3B2A1A]">{formatLongDate(confirmation.visitDate)}</dd></div>}
                {confirmation.deliveryOption === "2" && (
                  <>
                    <div className="sm:col-span-2"><dt className="text-[#3B2A1A]/70">{L(COPY.address)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.address}</dd></div>
                    <div><dt className="text-[#3B2A1A]/70">{L(COPY.cityState)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.city} / {confirmation.state}</dd></div>
                    <div><dt className="text-[#3B2A1A]/70">{L(COPY.pincode)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.pincode}</dd></div>
                  </>
                )}
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.paymentMode)}</dt><dd className="font-semibold text-[#3B2A1A] uppercase">{confirmation.paymentLabel || confirmation.paymentMode}</dd></div>
              </dl>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={restart} className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-6 py-3 text-sm font-semibold" data-testid="seva-again">{t("sevas.bookAnother")}</button>
              </div>
            </div>
          </div>
        </section>
      )}
    </PageLayout>
  );
};

export default Sevas;
